import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import type { Product } from '../../../../services/storeService';
import { makeStyles } from '@fluentui/react-components';
import { PriceSection, getCurrentPrice } from '../PriceSection';

interface ProductCardProps {
    product: Product;
    onAddToCart: (product: Product) => void;
    hasMembership?: boolean;
    onClick?: () => void;
}

const useStyles = makeStyles({
    productCard: {
        backgroundColor: '#F4F0E7',
        borderRadius: '24px',
        boxShadow: 'none',
        overflow: 'hidden',
        transition: 'none',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        '&:hover': {
            transform: 'none',
            boxShadow: 'none',
        },
    },
    productLink: {
        display: 'flex',
        flexDirection: 'column',
        flex: '1',
        textDecoration: 'none',
        color: 'inherit',
        '&:hover': {
            textDecoration: 'none',
        },
    },
    productImage: {
        width: '100%',
        borderRadius: '24px',
        objectFit: 'cover',
        display: 'block',
    },
    productName: {
        fontSize: '20px',
        fontWeight: '700',
        margin: '16px 16px 8px 16px',
        color: '#333',
        lineHeight: '1.4',
        textAlign: 'center',
    },
    pricing: {
        flex: '1',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginLeft: '16px',
        marginRight: '16px',
        marginBottom: '8px',
    },
    addToCartButtonContainer: {
        display: 'flex',
        justifyContent: 'center',
        padding: '16px',
        marginTop: 'auto',
    },
    addToCartButton: {
        backgroundColor: '#C6A972',
        color: '#222222',
        border: 'none',
        padding: '12px 20px',
        borderRadius: '40px',
        cursor: 'pointer',
        fontSize: '16px',
        fontWeight: '700',
        fontFamily: 'Figtree',
        lineHeight: '19.20px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        transition: 'background-color 0.2s ease',
        display: 'inline-flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '10px',
        '&:hover': {
            backgroundColor: '#b8985b',
        },
        '&:disabled': {
            backgroundColor: '#e0e0e0',
            color: '#999999',
            cursor: 'not-allowed',
        },
    },
});

export const ProductCard: React.FC<ProductCardProps> = ({
    product,
    onAddToCart,
    hasMembership = false,
    onClick,
}) => {
    const styles = useStyles();
    const [adding, setAdding] = useState(false);

    // Use first variation's pricing if variations exist, otherwise use product's pricing
    const displayProduct = (product.variations && product.variations.length > 0) 
        ? product.variations[0] 
        : product;

    const handleAddToCart = async (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        try {
            setAdding(true);
            const effectiveHasMembership = displayProduct.hasMembership ?? hasMembership;
            const currentPrice = getCurrentPrice(effectiveHasMembership, displayProduct.retailPrice, displayProduct.membershipPrice);

            // Use the displayProduct (which is the variation if it exists) for the cart
            const productWithCurrentPrice = {
                ...displayProduct,
                currentPrice: currentPrice
            };

            onAddToCart(productWithCurrentPrice);
        } catch (err) {
            console.error('Error adding product to cart:', err);
        } finally {
            setAdding(false);
        }
    };

    const handleCardClick = () => {
        if (onClick) {
            onClick();
        }
    };

    const productUrl = `/shop/product/${product.slug}`;
    const displayImageUrl = (product.variations && product.variations.length > 0) 
        ? product.variations[0].imageUrl 
        : product.imageUrl;

    return (
        <div className={styles.productCard}>
            <Link
                to={productUrl}
                className={styles.productLink}
                onClick={handleCardClick}
            >
                <img
                    src={displayImageUrl || '/default-product.jpg'}
                    alt={product.name}
                    className={styles.productImage}
                />
                <h2 className={styles.productName}>{product.name}</h2>
                <div className={styles.pricing}>
                    <PriceSection
                        hasMembership={displayProduct.hasMembership ?? hasMembership}
                        retailPrice={displayProduct.retailPrice}
                        membershipPrice={displayProduct.membershipPrice}
                        showMembershipLabel={true}
                        size="normal"
                        align="center"
                    />
                </div>
            </Link>
            <div className={styles.addToCartButtonContainer}>
                <button
                    onClick={handleAddToCart}
                    disabled={adding}
                    className={styles.addToCartButton}
                >
                    {adding ? 'Adding...' : 'Add to cart'}
                </button>
            </div>
        </div>
    );
};
